import { useCallback, useEffect, useMemo, useState } from 'react';
import { CONTROL_KEY, META_KEY, SHIFT_KEY } from 'data/global';
import { isEscapePressed, isOpenDrawerOrModal } from 'utils';

const A = ['a', 'A'];
const SHIFT = [SHIFT_KEY];
const CONTROL_COMMAND = [META_KEY, CONTROL_KEY];

export default function useSelect<T extends { _id: string }>(
  selectableItems: T[],
) {
  const [selected, setSelected] = useState<T[]>([]);
  const [shiftSelected, setShiftSelected] = useState<string>(null);

  const [isControlPressed, setIsControlPressed] = useState(false);
  const [isShiftPressed, setIsShiftPressed] = useState(false);
  const [isAPressed, setIsAPressed] = useState(false);

  const isSelectedMode = !!selected.length;

  const isSingleSelected = useMemo(
    () => selected.length === 1,
    [selected?.length],
  );

  const isAllSelected = useMemo(
    () => !!selected.length && selected.length === selectableItems.length,
    [selectableItems.length, selected.length],
  );

  const removeAll = useCallback(() => {
    setSelected([]);
  }, []);

  const selectAll = useCallback(() => {
    setSelected(selectableItems);
  }, [selectableItems]);

  const updateSelected = useCallback((val: T) => {
    setSelected((prev) =>
      prev.some(({ _id }) => _id === val?._id)
        ? prev.filter(({ _id }) => _id !== val?._id)
        : [...prev, val],
    );
  }, []);

  const updateSelectedValue = useCallback(
    (val: Partial<T>) =>
      setSelected((prev) =>
        prev.map((el) => (el._id === val._id ? { ...el, ...val } : el)),
      ),
    [],
  );

  const onSelect = useCallback(
    (selected: T) => {
      setSelected((prev) => {
        if (!isControlPressed && !isShiftPressed) {
          setShiftSelected(selected?._id);
          return [selected];
        }

        if (isControlPressed) {
          setShiftSelected(selected?._id);
          return prev.some(({ _id }) => _id === selected?._id)
            ? prev.filter((node) => node._id !== selected?._id)
            : [...prev, selected];
        }

        if (!shiftSelected) {
          setShiftSelected(selected?._id);
          return [selected];
        }

        const selectedIndex = selectableItems.findIndex(
          ({ _id }) => _id === selected?._id,
        );

        const lastSelectedIndex = selectableItems.findIndex(
          ({ _id }) => _id === shiftSelected,
        );

        return selectableItems.slice(
          Math.min(lastSelectedIndex, selectedIndex),
          Math.max(lastSelectedIndex, selectedIndex) + 1,
        );
      });
    },
    [isControlPressed, isShiftPressed, shiftSelected, selectableItems],
  );

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (CONTROL_COMMAND.includes(e.key)) {
        setIsControlPressed(true);
      }

      if (SHIFT.includes(e.key)) {
        setIsShiftPressed(true);
      }

      if (A.includes(e.key)) {
        setIsAPressed(true);

        if (isControlPressed) {
          // prevent select entire text
          e.preventDefault();
        }
      }

      if (isEscapePressed(e) && !isOpenDrawerOrModal()) {
        setSelected([]);
      }
    },
    [isControlPressed],
  );

  const onKeyUp = useCallback((e: KeyboardEvent) => {
    if (CONTROL_COMMAND.includes(e.key)) {
      setIsControlPressed(false);
    }

    if (SHIFT.includes(e.key)) {
      setIsShiftPressed(false);
    }

    if (A.includes(e.key)) {
      setIsAPressed(false);
    }
  }, []);

  // select all CTRL/CMD + A
  useEffect(() => {
    if (isAPressed && isControlPressed) {
      setSelected(selectableItems);

      setIsAPressed(false);
      setIsControlPressed(false);
      setIsShiftPressed(false);
    }
  }, [isAPressed, isControlPressed, selectableItems]);

  useEffect(() => {
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
    };
  }, [onKeyDown, onKeyUp]);

  return {
    selected,
    shiftSelected,
    isAllSelected,
    isSingleSelected,
    isSelectedMode,
    setSelected,
    setShiftSelected,
    removeAll,
    selectAll,
    updateSelected,
    onSelect,
    updateSelectedValue,
  };
}
